import { EffectLineObject } from '@phaser/objects/EffectLine.object';
import { SPAWN_DURATION } from '@phaser/behaviors/Card.behavior';
import { EnemyObject } from '@phaser/objects/Enemy.object';
import { LootObject } from '@phaser/objects/Loot.object';
import { initGlobalRegistry } from '@api/globalRegistry';
import { sendMessage, timeDiff } from '@api/websocket';
import { useGameStore, useAppStore } from '@stores';
import { Game } from '@phaser/scenes/Game';
import * as Shared from '@shared/lib';
import i18next from 'i18next';

export class MultiplayerSystem {
  private _scene: Game = Game.current;

  constructor() {
    this._registerEvents();
  }

  public ready() {
    sendMessage(new Shared.PlayerReadyMessage({}));
  }

  public destroy() {
    Shared.MessageRegistry.emitter.removeAllListeners();
    initGlobalRegistry();
  }

  public continue() {
    sendMessage(new Shared.ContinueMessage({}));
  }

  public leaveGame() {
    sendMessage(new Shared.LeaveGameMessage({}));
  }

  public stopShooting() {
    sendMessage(new Shared.StopShootingMessage({}));
  }

  public startShooting(pos: number) {
    sendMessage(new Shared.StartShootingMessage({ pos }));
  }

  public changeShootingPosition(pos: number) {
    sendMessage(new Shared.ChangeShootingPositionMessage({ pos }));
  }

  public gotFish(id: number, amount: number) {
    const drops = [new Shared.LootDropMessage({
      id,
      amount,
      type: Shared.LootDropType.Item
    })];
    sendMessage(new Shared.GotFishMessage({ drops }));
  }

  public minedOre(id: number, amount: number) {
    const drops = [new Shared.LootDropMessage({
      id,
      amount,
      type: Shared.LootDropType.Item
    })];
    sendMessage(new Shared.MinedOreMessage({ drops }));
  }

  public stopHacking(cardId: string, success: boolean) {
    sendMessage(new Shared.StopHackingMessage({ cardId, success }));
  }

  private _registerEvents() {
    Shared.MessageRegistry.emitter.on(Shared.ShowMiningMessage.Opcode, () => {
      useGameStore.setState({ isLoading: false });
      this._scene.miningMinigame.start();
    });

    Shared.MessageRegistry.emitter.on(Shared.ShowFishingMessage.Opcode, () => {
      useGameStore.setState({ isLoading: false });
      this._scene.fishingMinigame.start();
    });

    Shared.MessageRegistry.emitter.on(Shared.ShowContinueMessage.Opcode, () => {
      this._scene.continueSystem.show();
    });

    Shared.MessageRegistry.emitter.on(Shared.CardSyncMessage.Opcode, (data: Shared.CardSyncMessage) => {
      const card = this._scene.cards.find((c) => c.id === data.id);
      if (card) card.sync(data);
    });

    Shared.MessageRegistry.emitter.on(Shared.LobbyLeftMessage.Opcode, (data: Shared.LobbyLeftMessage) => {
      if (data.id === this._scene.id) return;
      this._scene.partySystem.playerLeft(data.id);
    });

    Shared.MessageRegistry.emitter.on(Shared.GameEndedMessage.Opcode, (data: Shared.GameEndedMessage) => {
      this._scene.destroy();
      this._scene.sys.game.destroy(true);
  
      useGameStore.getState().setGameEndedState(data);
      useAppStore.getState().setTab('navigation');
    });

    Shared.MessageRegistry.emitter.on(Shared.CardSpawnMessage.Opcode, (data: Shared.CardSpawnMessage) => {
      if (this._scene.deadIds.includes(data.core.id)) return;

      this._scene.miningMinigame.destroy();
      this._scene.fishingMinigame.destroy();
  
      setTimeout(() => {
        useGameStore.setState({ isLoading: false });
        const card = data.core.type === 'enemy'
          ? new EnemyObject(data)
          : new LootObject(data);
        this._scene.cards.push(card);
      }, data.core.spawnAt - timeDiff() - SPAWN_DURATION);
    });

    Shared.MessageRegistry.emitter.on(Shared.CardDeathMessage.Opcode, (data: Shared.CardDeathMessage) => {
      this._scene.deadIds.push(data.id);
  
      const card = this._scene.cards.find((c) => c.id === data.id);
      if (!card) return;
  
      card.die(data.drops);
      this._scene.gameModeSystem.onCardDeath(card);
      this._scene.cards = this._scene.cards.filter((c) => c.id !== data.id);
    });

    Shared.MessageRegistry.emitter.on(Shared.CardAttackMessage.Opcode, (data: Shared.CardAttackMessage) => {
      const enemy = this._scene.cards.find((c) => c.id === data.id);
      if (enemy && enemy instanceof EnemyObject) enemy.attack(data);
    });

    Shared.MessageRegistry.emitter.on(Shared.ActiveCritMessage.Opcode, (data: Shared.ActiveCritMessage) => {
      this._scene.cards.find((c) => c.id === data.id)?.onActiveCrit();
    });

    Shared.MessageRegistry.emitter.on(Shared.AbilityUseMessage.Opcode, (data: Shared.AbilityUseMessage) => {
      this._scene.partySystem.onAbilityUse(data);
  
      const card = this._scene.cards.find((c) => c.id === data.id);
      if (card) card.onAbility(data.abilityCode);
  
      data.effects.forEach((effect) => {
        const duration = effect.duration - timeDiff();
        const effectCard = this._scene.cards.find((c) => c.id === effect.targetId);
        if (effectCard) effectCard.onEffect(effect.effectCode, duration);

        if (card && effectCard && card.id !== effectCard.id) {
          const eP = { x: effectCard.container.x, y: effectCard.container.y };
          const cP = { x: card.container.x, y: card.container.y };
          new EffectLineObject(cP, eP);
        }
      });
    });

    Shared.MessageRegistry.emitter.on(Shared.PlayerShootMessage.Opcode, (data: Shared.PlayerShootMessage) => {
      this._scene.attackSystem.shoot(data);
    });

    Shared.MessageRegistry.emitter.on(Shared.PlayerSyncMessage.Opcode, (data: Shared.PlayerSyncMessage) => {
      if (data.respawnCountdown && this._scene.id.startsWith(data.id))
        this._scene.respawnSystem.respawnPlayer(data.respawnCountdown);
      this._scene.partySystem.onPlayerSync(data);
    });

    Shared.MessageRegistry.emitter.on(Shared.StartHackingMessage.Opcode, (data: Shared.StartHackingMessage) => {
      if (data.playerId === this._scene.id) {
        this._scene.minigameSystem.startHacking(data.cardId);
      }
      this._scene.cards.find((c) => c.id === data.cardId)?.showHackingIndicator();
    });

    Shared.MessageRegistry.emitter.on(Shared.AnnouncementMessage.Opcode, (data: Shared.AnnouncementMessage) => {
      useGameStore.setState({ isLoading: false });
      const message = i18next.t(`Game.Announcements.${data.type}`, { ...data });
      this._scene.announceSystem.announce(message);
    });

    Shared.MessageRegistry.emitter.on(Shared.SetSurvivalTimerMessage.Opcode, (data: Shared.SetSurvivalTimerMessage) => {
      this._scene.gameModeSystem.setSurvivalTimer(data.time);
    });

    Shared.MessageRegistry.emitter.on(Shared.SetExterminateCountMessage.Opcode, (data: Shared.SetExterminateCountMessage) => {
      this._scene.gameModeSystem.setExterminateCount(data.enemiesLeft);
    });
  }
}